import { Col, Row, Typography } from 'antd';
import { Loader } from 'components/common/Loader';
import WimsicalError from 'components/common/WimsicalError/WimsicalError';
import NoResults from 'components/common/noResults/noResults';
import { useFormikContext } from 'formik';
import { formatErrorMessage } from 'helpers/formatErrorMessage';
import { AcuityContext } from 'models/Application';
import { QueryErrorModel } from 'models/ErrorModel';
import { InlineStylesModel } from 'models/InlineStylesModel';
import { SparingRequestPayload } from 'models/SparingRequests';
import { useEffect } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useGetCurrentDivisionQuery } from 'redux/services/abbyCadabby/abbyCadabbyApi';
import { useGetAddressesQuery } from 'redux/services/chuckieSue/addressesApi';
import { incrementOffset, setAddressTypesInclude } from 'redux/services/chuckieSue/addressesParams';
import { GetAddressesParams, LocationAddressTypes } from 'redux/services/chuckieSue/models/addresses';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { LocationCard, SelectedLocationCard } from './components';

const styles: InlineStylesModel = {
  container: {
    flexGrow: 1,
    alignContent: 'flex-start'
  },
  selectedContainer: {
    flex: 1,
    alignItems: 'flex-start'
  }
};

interface Props {
  addressType: 'shipping' | 'outage';
}

export const ShippingLocationsGrid = ({ addressType }: Props): JSX.Element => {
  /* ******************** Hooks ******************** */
  const { data: divisionData } = useGetCurrentDivisionQuery();
  const { values } = useFormikContext<SparingRequestPayload>();

  const { chuckieSueAddressesParams } = useAppSelector((state) => state);
  const { acuityContext } = useAppSelector((state) => state.app);
  const { fullShippingAddress } = useAppSelector((state) => state.form);

  const { configuration } = acuityContext as AcuityContext;
  const areAllValuesUndefined = (obj: GetAddressesParams): boolean => {
    const filterOutParams = (obj: GetAddressesParams, paramsToExclude: string[]): Partial<GetAddressesParams> => {
      const newObj = { ...obj };

      paramsToExclude.forEach((param) => {
        const val = param as keyof GetAddressesParams;

        delete newObj[val];
      });

      return newObj;
    };

    return Object.values(filterOutParams(obj, ['offset', 'addressTypesInclude'])).every((val) => val === undefined);
  };

  const areAddressesNotDisplayed = Boolean(areAllValuesUndefined(chuckieSueAddressesParams) && !configuration?.addressDetails.displayAddressesByDefault);

  const { data, isLoading, isError, isFetching, error } = useGetAddressesQuery(
    {
      ...chuckieSueAddressesParams,
      addressTypesInclude: [LocationAddressTypes.Delivery]
    },
    { skip: areAddressesNotDisplayed }
  );

  const dispatch = useAppDispatch();

  /* ******************** Functions / Variables ******************** */
  const getMore = (): void => {
    dispatch(incrementOffset());
  };

  /* ******************** Effects ******************** */
  useEffect(() => {
    return () => {
      dispatch(setAddressTypesInclude(undefined));
    };
  }, [addressType, dispatch, divisionData]);

  useEffect(() => {
    dispatch(setAddressTypesInclude([LocationAddressTypes.Delivery]));
  }, []);

  if (areAllValuesUndefined(chuckieSueAddressesParams) && !configuration?.addressDetails.displayAddressesByDefault && !values.shippingLocationId)
    return (
      <Row justify="center" style={{ marginTop: 25 }}>
        <Typography.Text style={{ fontSize: 20 }}>Please use filters above to begin search..</Typography.Text>
      </Row>
    );

  /* ******************** Error Render ******************** */
  if (isLoading || (isFetching && chuckieSueAddressesParams.offset === 0)) {
    return (
      <Row style={styles.container} gutter={[16, 16]}>
        <Loader />
      </Row>
    );
  }

  if (isError) {
    const err = error as QueryErrorModel;

    return <WimsicalError title={formatErrorMessage(typeof err.status === 'number' ? err.status : 400)} subTitle={err?.data?.errorMessage ? err.data.errorMessage : ''} statusCode={err.status} />;
  }
  /* ******************** No Results Render ******************** */
  if (data?.totalCount === 0) {
    return <NoResults errorMessage={'No Locations Found'} />;
  }

  const selectedCardJSX = (
    <Col key={values.shippingLocationId} xs={24} lg={12}>
      {fullShippingAddress ? <SelectedLocationCard address={fullShippingAddress} addressType="shipping" /> : null}
    </Col>
  );

  const locationCardsJSX = data?.data
    .filter((address) => address.id !== values?.shippingLocationId)
    .map((address) => {
      return (
        <Col xs={24} lg={12} key={address.id}>
          <LocationCard addressType={addressType} address={address} />
        </Col>
      );
    });

  const hasMore = (data?.totalCount ?? 0) > chuckieSueAddressesParams.offset && (data?.totalCount ?? 0) > 25 && !values.shippingLocationId;

  return (
    <InfiniteScroll style={{ width: '100%', marginTop: '8px', overflow: 'visible' }} next={getMore} hasMore={hasMore} loader={<Loader />} dataLength={data?.data.length ?? 0}>
      <Row gutter={[16, 16]}>{values.shippingLocationId && values.shippingLocationId !== null ? [...[selectedCardJSX], ...[locationCardsJSX ?? []]] : locationCardsJSX}</Row>
    </InfiniteScroll>
  );
};
